import qs from "qs";
import { emptyApi } from "./emptyApi";
import { JournalEntry } from "types/Models/journalEntry";
import { Pagination } from "types/Models/pagination";
import { RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T } from "types/contants/recurringJournalEntryUpdateTypes";
import {
  JESortColumnType,
  JOURNAL_ENTRY_STATUS_TYPE,
} from "constants/journalEntrySources";

export type JournalEntryParams = {
  pageNum?: number;
  searchTerm?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  sortCol?: JESortColumnType;
  sortOrder?: "ASC" | "DSC";
  ledger_types?: string | null;
  accounting_method?: string | null;
  source?: string | null;
  recurring_entries_only?: boolean | null;
  hide_future_entries?: boolean | null;
  status?: string | null;
  fetch_page_num?: boolean;
  jump_ledger_entry_id?: string;
  pageSize?: number;
};

export type JEItem = {
  description: string;
  vendor: string;
  category: { uuid: string; identifier: string; name: string } | null;
  amount: number;
};

export type UnbalanceJE = {
  date: string;
  journal_no: string;
  journal_title: string;
  journal_items: JEItem[];
  manual_entries?: JEItem[];
};

export const journalEntryFilterValuesToParams = (
  values: JournalEntryParams
) => {
  return qs.stringify(
    {
      page_num: values.pageNum,
      search_term: values.searchTerm,
      start_date: values.startDate,
      end_date: values.endDate,
      sort_col: values.sortCol,
      sort_order: values.sortOrder,
      ledger_types: values.ledger_types,
      basis: values.accounting_method,
      source: values.source,
      only_recurring_entries: values.recurring_entries_only,
      hide_future_entries: values.hide_future_entries,
      ledger_entry_status: values.status,
      fetch_page_num: values.fetch_page_num,
      jump_ledger_entry_id: values.jump_ledger_entry_id,
      page_size: values.pageSize,
    },
    { skipNulls: true, addQueryPrefix: true }
  );
};

export const journalEntries = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllJournalEntries: build.query<
      { journal_entries: JournalEntry[]; page_number?: number } & Pagination,
      {
        groupId: string;
        entityId: string;
      } & JournalEntryParams
    >({
      query: ({ groupId, entityId, ...params }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entries/${journalEntryFilterValuesToParams(
            params
          )}`,
        };
      },
      providesTags: (result) => (result ? ["JOURNAL_ENTRIES"] : []),
    }),

    bulkDeleteJournalEntries: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        entryIds: string[];
        event_update_type: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T;
      }
    >({
      query: ({ groupId, entityId, entryIds, event_update_type }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entries/`,
          method: "DELETE",
          body: {
            entry_ids: entryIds,
            event_update_type,
          },
        };
      },
      invalidatesTags: ["JOURNAL_ENTRIES", "PAYROLL"],
    }),
    bulkUpdateJournalEntryStatus: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        ledger_entry_uuids: string[];
        status: JOURNAL_ENTRY_STATUS_TYPE;
      }
    >({
      query: ({ groupId, entityId, ledger_entry_uuids, status }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entry/`,
        method: "PATCH",
        body: { ledger_entry_uuids, status },
      }),
      invalidatesTags: ["JOURNAL_ENTRIES", "GENERAL_LEDGER"],
    }),

    uploadBulkJournalEntry: build.mutation<
      {
        bulk_file_id: string;
      },
      {
        entityId: string;
        file: File;
      }
    >({
      query: ({ entityId, file }) => {
        const formData = new FormData();
        formData.append("excel", file);

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bulk-ledger-entry-upload/`,
          method: "POST",
          body: formData,
        };
      },
    }),
    getBulkJournalEntryReview: build.query<
      UnbalanceJE[],
      { entityId: string; bulkFileId: string }
    >({
      query: ({ entityId, bulkFileId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/bulk-ledger-entry-upload/?bulk_file_id=${bulkFileId}`,
      }),
    }),

    populateBulkJournalEntry: build.mutation<
      any,
      {
        entityId: string;
        bulkFileId: string;
        journalEntries: UnbalanceJE[];
      }
    >({
      query: ({ entityId, bulkFileId, journalEntries }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/populate-bulk-ledger-entry/`,
          method: "POST",
          body: {
            bulk_file_id: bulkFileId,
            journal_entries: journalEntries,
          },
        };
      },
      invalidatesTags: ["JOURNAL_ENTRIES"],
    }),

    getJEBulkTemplate: build.query<
      { download_url: string },
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/templates/journal-entries/`,
      }),
    }),

    exportJournalEntries: build.query<
      { document_id: string },
      {
        groupId: string;
        entityId: string;
        export_to_mail?: boolean;
        ledger_entry_id?: string;
      } & JournalEntryParams
    >({
      query: ({
        groupId,
        entityId,
        export_to_mail,
        ledger_entry_id,
        ...params
      }) => {
        const filterParams = qs.parse(
          journalEntryFilterValuesToParams(params).replace(/^\?/, "")
        );
        const queryParams = qs.stringify(
          {
            ...filterParams,
            export_to_mail,
            ledger_entry_id,
            export: true,
          },
          { addQueryPrefix: true, skipNulls: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entries/${queryParams}`,
        };
      },
    }),
  }),
});

export const {
  useGetAllJournalEntriesQuery,
  useLazyGetAllJournalEntriesQuery,
  useBulkDeleteJournalEntriesMutation,
  useBulkUpdateJournalEntryStatusMutation,
  useUploadBulkJournalEntryMutation,
  usePopulateBulkJournalEntryMutation,
  useGetBulkJournalEntryReviewQuery,
  useLazyGetBulkJournalEntryReviewQuery,
  useLazyGetJEBulkTemplateQuery,
  useLazyExportJournalEntriesQuery,
} = journalEntries;
